// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * What the symbols on a logistics plan mean.
 *
 * A plan leaves the screen constantly — printed for the trailer wall, pasted
 * into a subcontractor's scope, attached to a permit application — and on
 * paper the legend in the side panel is not there. A sheet with TRL, DMP and
 * CW on it and nothing to decode them is a sheet that has to be explained in
 * person every time.
 *
 * Only what is actually on the drawing is listed. A legend that names every
 * symbol the software knows about is a catalogue, not a key.
 */

import type { SiteLogistics, SiteItemType, SiteLineType } from "./logistics";
import { SITE_ICON_CONFIG, SITE_LINE_CONFIG, SITE_AREA_CONFIG } from "./logistics";

export interface LegendRow {
  kind: "item" | "line" | "area";
  /** The abbreviation drawn on the plan, for items. */
  abbr?: string;
  label: string;
  color: string;
  /** SVG dash pattern, for lines. */
  dash?: string;
  /** How many of this are on the plan; omitted when there is one. */
  count?: number;
}

const tally = <T extends string>(xs: T[]): Array<[T, number]> => {
  const n = new Map<T, number>();
  for (const x of xs) n.set(x, (n.get(x) || 0) + 1);
  return [...n.entries()];
};

/**
 * The legend for a plan, in the order it reads best: the things placed on it,
 * then the lines drawn across it, then the areas marked out on it.
 */
export function legendRows(sl: SiteLogistics | null | undefined): LegendRow[] {
  if (!sl) return [];
  const rows: LegendRow[] = [];

  for (const [type, count] of tally((sl.icons || []).map((i) => i.type as SiteItemType))) {
    const cfg = SITE_ICON_CONFIG[type];
    if (!cfg) continue;
    // An item the user has recoloured is shown in the colour it was drawn
    // in, not the one the type ships with -- the legend decodes the sheet in
    // front of you, not the software's idea of it.
    const drawn = (sl.icons || []).find((i) => i.type === type);
    rows.push({ kind: "item", abbr: cfg.abbr, label: cfg.label, color: drawn?.color || cfg.color,
      ...(count > 1 ? { count } : {}) });
  }

  // A custom line is whatever this job needed — fencing, a sleeve route, a
  // mat run — and it is named by what the drawer called it rather than by its
  // type. So custom lines are grouped by their label: two that say different
  // things get two rows, and a key that read "Custom Line x2" would be no key
  // at all.
  const lineKey = (l: { lineType: string; label?: string }) =>
    l.lineType === "custom" ? `custom:${String(l.label ?? "").trim()}` : l.lineType;

  for (const [key, count] of tally((sl.lines || []).map(lineKey))) {
    const drawn = (sl.lines || []).find((l) => lineKey(l) === key);
    if (!drawn) continue;
    const cfg = SITE_LINE_CONFIG[drawn.lineType as SiteLineType];
    if (!cfg) continue;
    // A line the user has recoloured or redashed is shown as it was drawn,
    // not as the type ships.
    const label = drawn.lineType === "custom"
      ? (String(drawn.label ?? "").trim() || cfg.label)
      : cfg.label;
    const typeDash = cfg.dash === "none" ? "" : cfg.dash;
    rows.push({ kind: "line", label, color: drawn.strokeColor || cfg.color,
      dash: drawn.strokeDash !== undefined ? drawn.strokeDash : typeDash,
      ...(count > 1 ? { count } : {}) });
  }

  // Areas group by type *and* by what they were called, for the same reason
  // lines do: a plan with a laydown yard and a crane pad both drawn as custom
  // areas used to produce one row reading "Custom Area x2", which names
  // neither of them. Two areas that genuinely share a name still merge.
  const areaKey = (a: { areaType: string; label?: string }) =>
    `${a.areaType} ${String(a.label ?? "").trim()}`;

  for (const [key, count] of tally((sl.areas || []).map(areaKey))) {
    const drawn = (sl.areas || []).find((a) => areaKey(a) === key);
    if (!drawn) continue;
    const cfg = SITE_AREA_CONFIG[drawn.areaType] || SITE_AREA_CONFIG["custom-area"];
    rows.push({ kind: "area",
      label: String(drawn.label ?? "").trim() || cfg.label,
      color: drawn.fillColor || drawn.color || cfg.color,
      ...(count > 1 ? { count } : {}) });
  }

  return rows;
}

/** The row's text as it appears on the sheet. */
export function rowText(row: LegendRow): string {
  return row.count && row.count > 1 ? `${row.label} ×${row.count}` : row.label;
}

/**
 * How tall a legend strip needs to be for a given number of columns.
 *
 * Returned rather than guessed so the sheet can be sized before anything is
 * drawn on it, and the plan above it never ends up overlapped.
 */
export function legendHeight(rowCount: number, columns: number, opts?: {
  rowH?: number; headerH?: number; padding?: number;
}): number {
  const rowH = opts?.rowH ?? 18;
  // The default is the key's own header. A sheet that also carries a caption
  // -- the plan's name and dates -- passes a taller one; the two numbers have
  // to be the same or the strip draws over the drawing.
  const headerH = opts?.headerH ?? 22;
  const pad = opts?.padding ?? 10;
  if (rowCount <= 0) return 0;
  const cols = Math.max(1, columns);
  return headerH + Math.ceil(rowCount / cols) * rowH + pad * 2;
}
